import TabSelect from '@/creditflowresearch/ui/components/bondscreener/TabSelect';
import Flex from '@creditflowresearch/uilib/Flex';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import {
  useCorporateBondScreenerContext,
} from '@/creditflowresearch/application/CorporateBondScreener/CorporateBondScreenerContext';
import { BondScreenerExportButton } from '@/creditflowresearch/ui/common/components/molecules/BondScreenerExportButton';
import FiltersBtn from '@/creditflowresearch/ui/common/components/organisms/FiltersBtn/FiltersBtn';
import { BondScreenerSearchInput } from '@/creditflowresearch/ui/common/components/molecules/BondScreenerSearchInput';
import useExportCorporateBondScreener from './ActionBar/useExportCorporateBondScreener';
import WithExportRestriction from '@/creditflowresearch/ui/common/components/molecules/WithExportRestriction';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { UserPlanFeature } from '@creditflowresearch/user-plan/vo/IUserPlanResponse';
import RequestServiceModalProvider from '@/creditflowresearch/application/RequestServiceModalContext';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { useIsGuest } from '@/creditflowresearch/application/User/access';
import useCorporateBondScreenerFiltersCount from './useCorporateBondScreenerFiltersCount';
import {
  BondScreenerHeaderViewSelect,
} from '@/creditflowresearch/ui/components/bondscreener/BondScreenerHeaderViewSelect';
import {
  CORPORATE_BS_TABS_CONFIG,
  type CorporateBondTabType,
} from './corporateBondScreenerTabsConfig';

import styles from './corporateBondScreenerHeader.module.scss';

interface ICorporateBondScreenerHeaderProps {
  activeTabId: CorporateBondTabType,
  hasSearch: boolean,
  onTabChange: (tabId: CorporateBondTabType) => void,
  onOpenSidebar: () => void,
}

const CorporateBondScreenerHeader = ({
  activeTabId,
  hasSearch,
  onTabChange,
  onOpenSidebar,
}: ICorporateBondScreenerHeaderProps) => {
  const { checkAccessByUserPlan, features } = useUserPlanContext();
  const { checkAccessByUserRegions, checkAccessByUserProducts } = usePageFiltersContext();
  // TODO: remove when the European market opening period ends
  const isViewRestricted = useIsGuest();

  const checks = [
    checkAccessByUserPlan(UserPlanType.PRO),
    checkAccessByUserRegions(),
    checkAccessByUserProducts(),
  ];

  const { debouncedSearch, debouncedSearchString } = useCorporateBondScreenerContext();
  const { isExporting, isGenerating, onExportAll, onExportSelected } = useExportCorporateBondScreener();
  const filtersCount = useCorporateBondScreenerFiltersCount();

  return (
    <Flex
      direction={ { sm: 'column-reverse', md: 'column-reverse', xl: 'row' } }
      justify={ { xl: 'flex-end' } }
      align={ { sm: 'stretch', md: 'stretch', xl: 'center' } }
      gap={ 10 }
    >
      { hasSearch && (
        <BondScreenerSearchInput
          initialSearchValue={ debouncedSearchString }
          className={ styles.search }
          dataTest="corporateBondScreenerSearchInput"
          placeholder="Filter by Issuer or Ticker"
          onSearch={ debouncedSearch }
        />
      ) }

      <Flex dataTest="corporateBondScreenerActions" gap={ 10 }>
        <div className={ styles.tabSelect }>
          <TabSelect<CorporateBondTabType>
            tabs={ CORPORATE_BS_TABS_CONFIG }
            activeTab={ activeTabId }
            onChangeTab={ onTabChange }
            instanceId="corporate-bs-tab-select"
            dataTest="corporateBondScreenerTabSelect"
          />
        </div>
        <RequestServiceModalProvider>
          <WithExportRestriction
            conditions={ [features.includes(UserPlanFeature.XlsExport), ...checks] }
          >
            { ({ isRestricted, toggleExplanation }) => (
              <BondScreenerExportButton
                showDropdown
                dataTest="corporateBondScreenerExportButton"
                onExportAll={ isRestricted ? toggleExplanation : onExportAll }
                onExportSelected={ isRestricted ? toggleExplanation : onExportSelected }
                isDisabled={ isExporting }
                isLoading={ isGenerating }
              />
            ) }
          </WithExportRestriction>
        </RequestServiceModalProvider>
        <div className={ styles.selectView }>
          <BondScreenerHeaderViewSelect
            dataTest="corporateBondScreenerSelectViewBtn"
            isRestricted={ isViewRestricted }
          />
        </div>
      </Flex>

      <Tooltip content="Customize Data and UI" asChild>
        <FiltersBtn
          filtersCount={ filtersCount }
          onClick={ onOpenSidebar }
          dataTest="filtersButton"
          className={ styles.filtersBtn }
          label="Customization"
          labelClassName={ styles.filtersBtnLabel }
          desktopIconOnly
        />
      </Tooltip>
    </Flex>
  );
};

export default CorporateBondScreenerHeader;
